import { ThemeContext } from '@susu/custom-made/providers';
import {
  actions,
  actions as missingSelectionValuesActions,
} from '@susu/custom-made/providers/reducers/missingSelectionValues';
import { actions as optionActions } from '@susu/custom-made/providers/reducers/option';
import { Level } from '@susu/custom-made/utils/constants';
import type { Option, OptionValue } from '@susu/custom-made/utils/interfaces';
import { changeSection, getSectionByConfigurationType } from '@susu/custom-made/utils/navigation';
import type {
  MissingMonogramValuesPerConfig,
  MissingOptionsPerConfiguration,
  MissingOptionValuesPerConfiguration,
} from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import {
  getFirstSectionWithMissingValues,
  getOptionAndMatchingOptionGroupByOptionId,
  getSelectedIdsWithMissingValues,
} from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { isAdditonalProduct, isObjectEmpty } from '@susu/custom-made/utils/utils';
import { useCallback, useContext } from 'react';

export const useMissingValues = () => {
  const { state, dispatch } = useContext(ThemeContext);

  const getSectionsWithMissingValues = useCallback(() => {
    let sectionsWithMissingValues: string[] = [];

    if (state.missingOptionValuesPerConfig) {
      Object.keys(state.missingOptionValuesPerConfig).forEach(configurationTypeId => {
        const matchingSection = getSectionByConfigurationType(configurationTypeId);
        sectionsWithMissingValues = [...sectionsWithMissingValues, matchingSection];
      });
    }

    if (state.missingMonogramValuesPerConfig) {
      Object.keys(state.missingMonogramValuesPerConfig).forEach(configurationTypeId => {
        const matchingSection = getSectionByConfigurationType(configurationTypeId);
        sectionsWithMissingValues = [...sectionsWithMissingValues, matchingSection];
      });
    }

    if (state.missingOptionsPerConfig) {
      Object.keys(state.missingOptionsPerConfig).forEach(configurationTypeId => {
        const matchingSection = getSectionByConfigurationType(configurationTypeId);
        sectionsWithMissingValues = [...sectionsWithMissingValues, matchingSection];
      });
    }

    return sectionsWithMissingValues;
  }, [state.missingOptionValuesPerConfig, state.missingMonogramValuesPerConfig, state.missingOptionsPerConfig]);

  const isOptionWithoutSelection = useCallback(
    (option: Option, configurationType?: ConfigurationTypes) => {
      if (!configurationType) return false;

      const isOptionMissing = state.missingOptionsPerConfig?.[configurationType]?.[option.id];
      return !!isOptionMissing;
    },
    [state.missingOptionsPerConfig],
  );

  const closeMissingOptionValuesPanel = useCallback(() => {
    dispatch({
      type: missingSelectionValuesActions.SET_SHOW_MISSING_OPTIONS_PANEL,
      payload: false,
    });
  }, [dispatch]);

  const openMissingOptionValuesPanel = useCallback(() => {
    dispatch({
      type: missingSelectionValuesActions.SET_SHOW_MISSING_OPTIONS_PANEL,
      payload: true,
    });
  }, [dispatch]);

  const closeMissingFabricModal = useCallback(() => {
    dispatch({
      type: missingSelectionValuesActions.SET_SHOW_MISSING_FABRIC_MODAL,
      payload: false,
    });
  }, [dispatch]);

  const setMissingOptionsPerConfig = useCallback(
    (missingOptionSelection: MissingOptionsPerConfiguration) => {
      dispatch({
        type: missingSelectionValuesActions.SET_MISSING_OPTIONS_PER_CONFIG,
        payload: missingOptionSelection,
      });
    },
    [dispatch],
  );

  const redirectToFirstRelevantSection = (
    missingOptionSelection:
      | MissingOptionValuesPerConfiguration
      | MissingMonogramValuesPerConfig
      | MissingOptionsPerConfiguration,
  ) => {
    const productMatchingSection = getFirstSectionWithMissingValues(missingOptionSelection);
    changeSection(productMatchingSection, Level.Group, dispatch);
  };

  const validateConfigurationOnSummary = (
    missingOptionSelection: MissingOptionsPerConfiguration,
    missingMonogramSelection: MissingMonogramValuesPerConfig,
  ) => {
    let isConfigurationValid = true;
    if (!isObjectEmpty(missingOptionSelection)) {
      setMissingOptionsPerConfig(missingOptionSelection);
    }

    if (!isObjectEmpty(missingMonogramSelection)) {
      setMissingMonogramValuesPerConfig(missingMonogramSelection);
      setMonogramPositionIdsWithMissingValues(missingMonogramSelection);
    }

    const missingSelection = !isObjectEmpty(missingOptionSelection) ? missingOptionSelection : missingMonogramSelection;

    if (!isObjectEmpty(missingSelection)) {
      isConfigurationValid = false;

      triggerSummaryValidationFlow();
      redirectToFirstRelevantSection(missingSelection);
      openMissingOptionValuesPanel();
    }

    return isConfigurationValid;
  };

  const triggerSummaryValidationFlow = () => {
    dispatch({
      type: actions.SET_IS_SUMMARY_VALIDATION_TRIGGERED,
      payload: true,
    });
  };

  const missingValueClickHandler = (e: React.BaseSyntheticEvent, options: Option[]) => {
    const { optionId, positionId, associatedSection } = e.currentTarget.dataset;

    if (optionId) {
      const matchingOptionAndGroup = getOptionAndMatchingOptionGroupByOptionId(+optionId, options);

      if (matchingOptionAndGroup?.option) {
        dispatch({
          type: optionActions.SET_SELECTED_OPTION,
          payload: matchingOptionAndGroup?.option,
        });
        matchingOptionAndGroup.optionGroup &&
          dispatch({
            type: optionActions.SET_SELECTED_OPTION_GROUP,
            payload: matchingOptionAndGroup.optionGroup,
          });

        changeSection(associatedSection, Level.Subgroup, dispatch);
      }
    }

    if (positionId) {
      changeSection(associatedSection, Level.Monogram, dispatch);
    }

    closeMissingOptionValuesPanel();
  };

  const areOptionMissingValuesEmpty = useCallback(
    (missingValues?: MissingOptionValuesPerConfiguration) => {
      const missingValuesToCheck = missingValues ?? state.missingOptionValuesPerConfig;

      if (!missingValuesToCheck || isObjectEmpty(missingValuesToCheck)) {
        return true;
      }

      return Object.entries(missingValuesToCheck).every(([configurationTypeId, missingOptionValue]) => {
        if (
          isAdditonalProduct(state.product!, configurationTypeId as ConfigurationTypes) &&
          !state.isAdditionalProductAdded[configurationTypeId]
        ) {
          return true;
        }
        return isObjectEmpty(missingOptionValue);
      });
    },
    [state.missingOptionValuesPerConfig, state.product, state.isAdditionalProductAdded],
  );

  const areMonogramMissingValuesEmpty = useCallback(
    (missingValues?: MissingMonogramValuesPerConfig) => {
      const missingValuesToCheck = missingValues ?? state.missingMonogramValuesPerConfig;

      if (!missingValuesToCheck || isObjectEmpty(missingValuesToCheck)) {
        return true;
      }

      return Object.values(missingValuesToCheck).every(missingMonogramValue => isObjectEmpty(missingMonogramValue));
    },
    [state.missingMonogramValuesPerConfig],
  );

  const setMissingOptionValuesPerConfig = useCallback(
    (missingOptionValuesSelection: MissingOptionValuesPerConfiguration) => {
      dispatch({
        type: missingSelectionValuesActions.SET_MISSING_OPTION_VALUES_PER_CONFIG,
        payload: missingOptionValuesSelection,
      });
    },
    [dispatch],
  );

  const areMissingOptionsEmpty = useCallback(
    (missingOptions?: MissingOptionsPerConfiguration) => {
      const missingValuesToCheck = missingOptions ?? state.missingOptionsPerConfig;

      if (!missingValuesToCheck || isObjectEmpty(missingValuesToCheck)) {
        return true;
      }

      return Object.entries(missingValuesToCheck).every(([configurationTypeId, missingOption]) => {
        if (
          isAdditonalProduct(state.product!, configurationTypeId as ConfigurationTypes) &&
          !state.isAdditionalProductAdded[configurationTypeId]
        ) {
          return true;
        }
        return isObjectEmpty(missingOption);
      });
    },
    [state.missingOptionsPerConfig, state.product, state.isAdditionalProductAdded],
  );

  const areAllConfigurationMissingValuesEmpty = useCallback(() => {
    return areOptionMissingValuesEmpty() && areMissingOptionsEmpty() && areMonogramMissingValuesEmpty();
  }, [areOptionMissingValuesEmpty, areMissingOptionsEmpty, areMonogramMissingValuesEmpty]);

  const removeOptionFromMissingValues = useCallback(
    (configurationTypeId: string, optionId: number) => {
      const isOptionStillMissing =
        state.missingOptionsPerConfig &&
        state.missingOptionsPerConfig[+configurationTypeId] &&
        state.missingOptionsPerConfig[+configurationTypeId][optionId];

      if (!isOptionStillMissing) {
        return;
      }

      const tmpMissingOptionsPerConfig = structuredClone(state.missingOptionsPerConfig);

      delete tmpMissingOptionsPerConfig[+configurationTypeId][optionId];

      if (isObjectEmpty(tmpMissingOptionsPerConfig[+configurationTypeId])) {
        delete tmpMissingOptionsPerConfig[+configurationTypeId];
      }

      setMissingOptionsPerConfig(tmpMissingOptionsPerConfig);
    },
    [state.missingOptionsPerConfig, setMissingOptionsPerConfig],
  );

  const removeConfigurationOptionValuesFromMissingValues = (configurationTypeId: string) => {
    const isOptionValuesStillMissing =
      state.missingOptionValuesPerConfig && state.missingOptionValuesPerConfig[+configurationTypeId];

    if (isOptionValuesStillMissing) {
      const tmpMissingOptionValuesPerConfig = structuredClone(state.missingOptionValuesPerConfig);

      delete tmpMissingOptionValuesPerConfig[+configurationTypeId];

      setMissingOptionValuesPerConfig(tmpMissingOptionValuesPerConfig);
    }
  };

  const removeSelectedOptionValueFromMissingValues = useCallback(
    (configurationTypeId: string, selectedOptionValue: OptionValue) => {
      removeOptionFromMissingValues(configurationTypeId, selectedOptionValue.optionId);

      const isOptionValueStillMissing =
        state.missingOptionValuesPerConfig &&
        state.missingOptionValuesPerConfig[+configurationTypeId] &&
        state.missingOptionValuesPerConfig[+configurationTypeId][selectedOptionValue.optionId];

      if (!isOptionValueStillMissing) {
        return;
      }

      const tmpMissingOptionValuesPerConfig = structuredClone(state.missingOptionValuesPerConfig);

      delete tmpMissingOptionValuesPerConfig[+configurationTypeId][selectedOptionValue.optionId];

      if (isObjectEmpty(tmpMissingOptionValuesPerConfig[+configurationTypeId])) {
        delete tmpMissingOptionValuesPerConfig[+configurationTypeId];
      }

      setMissingOptionValuesPerConfig(tmpMissingOptionValuesPerConfig);
    },
    [state.missingOptionValuesPerConfig, removeOptionFromMissingValues, setMissingOptionValuesPerConfig],
  );

  const removeSelectedMonogramFromMissingValues = (configurationTypeId: string, monogramPositionId: number) => {
    const isMonogramStillMissing =
      state.missingMonogramValuesPerConfig &&
      state.missingMonogramValuesPerConfig[+configurationTypeId] &&
      state.missingMonogramValuesPerConfig[+configurationTypeId][monogramPositionId];

    if (!isMonogramStillMissing) {
      return;
    }

    const tmpMissingMonogramValuesPerConfig = structuredClone(state.missingMonogramValuesPerConfig);

    delete tmpMissingMonogramValuesPerConfig[+configurationTypeId][monogramPositionId];

    if (isObjectEmpty(tmpMissingMonogramValuesPerConfig[+configurationTypeId])) {
      delete tmpMissingMonogramValuesPerConfig[+configurationTypeId];
    }

    setMissingMonogramValuesPerConfig(tmpMissingMonogramValuesPerConfig);
    setMonogramPositionIdsWithMissingValues(tmpMissingMonogramValuesPerConfig);
  };

  const setMonogramPositionIdsWithMissingValues = (missingValues: MissingMonogramValuesPerConfig) => {
    let allMissingPositionIds = getSelectedIdsWithMissingValues(missingValues);

    dispatch({
      type: missingSelectionValuesActions.SET_MONOGRAMS_WITH_MISSING_SELECTION,
      payload: allMissingPositionIds,
    });
  };

  const setMissingMonogramValuesPerConfig = (missingMonogramSelection: MissingMonogramValuesPerConfig) => {
    dispatch({
      type: missingSelectionValuesActions.SET_MISSING_MONOGRAM_VALUES_PER_CONFIG,
      payload: missingMonogramSelection,
    });
  };

  return {
    getSectionsWithMissingValues,
    closeMissingOptionValuesPanel,
    openMissingOptionValuesPanel,
    closeMissingFabricModal,
    redirectToFirstRelevantSection,
    missingValueClickHandler,
    areOptionMissingValuesEmpty,
    areMonogramMissingValuesEmpty,
    areAllConfigurationMissingValuesEmpty,
    removeSelectedOptionValueFromMissingValues,
    removeConfigurationOptionValuesFromMissingValues,
    removeSelectedMonogramFromMissingValues,
    isOptionWithoutSelection,
    setMonogramPositionIdsWithMissingValues,
    setMissingOptionValuesPerConfig,
    setMissingMonogramValuesPerConfig,
    validateConfigurationOnSummary,
  };
};
